/**
 * CategoryIcon -- maps category slug/name to its Lucide icon.
 *
 * Mirrors the PROVIDER_CONFIG in category-chips.tsx so icons are consistent
 * between the add sheet chips and everywhere else in the UI.
 *
 * getCategoryEmoji() is kept for the quick-add pending queue which renders
 * a plain string fallback — it now returns the slug itself so it degrades
 * gracefully. Callers that need a visual icon should use <CategoryIcon>.
 */

import React from 'react';
import {
  Film,
  Tv,
  BookOpen,
  Mic2,
  UtensilsCrossed,
  MapPin,
  Package,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Slug → Lucide icon map ────────────────────────────────────────────────────

const CATEGORY_ICON_MAP: Record<string, LucideIcon> = {
  movie:      Film,
  movies:     Film,
  'tv-show':  Tv,
  tv:         Tv,
  'tv show':  Tv,
  'tv shows': Tv,
  book:       BookOpen,
  books:      BookOpen,
  podcast:    Mic2,
  podcasts:   Mic2,
  restaurant: UtensilsCrossed,
  restaurants:UtensilsCrossed,
  place:      MapPin,
  places:     MapPin,
  other:      Package,
  misc:       Package,
};

const FALLBACK_ICON: LucideIcon = Package;

// ── Color map (unchanged) ─────────────────────────────────────────────────────

const CATEGORY_COLOR_MAP: Record<string, string> = {
  movie:       'var(--category-movies)',
  movies:      'var(--category-movies)',
  tv:          'var(--category-tv)',
  'tv show':   'var(--category-tv)',
  'tv shows':  'var(--category-tv)',
  'tv-show':   'var(--category-tv)',
  book:        'var(--category-books)',
  books:       'var(--category-books)',
  music:       'var(--category-music)',
  podcast:     'var(--category-podcasts)',
  podcasts:    'var(--category-podcasts)',
  restaurant:  'var(--category-restaurants)',
  restaurants: 'var(--category-restaurants)',
  recipe:      'var(--category-recipes)',
  recipes:     'var(--category-recipes)',
  place:       'var(--category-places)',
  places:      'var(--category-places)',
};

/** Returns the CSS color value for a category, or the "other" fallback. */
export function getCategoryColor(category?: string | null): string {
  if (!category) return 'var(--category-other)';
  return CATEGORY_COLOR_MAP[category.toLowerCase()] ?? 'var(--category-other)';
}

const CATEGORY_SHADOW_MAP: Record<string, string> = {
  movie:       'var(--category-movies-shadow)',
  movies:      'var(--category-movies-shadow)',
  tv:          'var(--category-tv-shadow)',
  'tv show':   'var(--category-tv-shadow)',
  'tv shows':  'var(--category-tv-shadow)',
  'tv-show':   'var(--category-tv-shadow)',
  book:        'var(--category-books-shadow)',
  books:       'var(--category-books-shadow)',
  music:       'var(--category-music-shadow)',
  podcast:     'var(--category-podcasts-shadow)',
  podcasts:    'var(--category-podcasts-shadow)',
  recipe:      'var(--category-recipes-shadow)',
  recipes:     'var(--category-recipes-shadow)',
  restaurant:  'var(--category-restaurants-shadow)',
  restaurants: 'var(--category-restaurants-shadow)',
  place:       'var(--category-places-shadow)',
  places:      'var(--category-places-shadow)',
};

/** Returns a darkened shadow color for use in retro hard-shadow card effects. */
export function getCategoryShadowColor(category?: string | null): string {
  if (!category) return 'var(--category-other-shadow)';
  return CATEGORY_SHADOW_MAP[category.toLowerCase()] ?? 'var(--category-other-shadow)';
}

// ── Size map ──────────────────────────────────────────────────────────────────

const SIZE_CLASSES = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-7 w-7',
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

interface CategoryEmojiProps {
  category?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
}

/** Renders the Lucide icon for a category slug/name. */
export function CategoryEmoji({ category, size = 'md', className = '', style }: CategoryEmojiProps) {
  const Icon = category
    ? (CATEGORY_ICON_MAP[category.toLowerCase()] ?? FALLBACK_ICON)
    : FALLBACK_ICON;

  return (
    <Icon
      className={cn(SIZE_CLASSES[size], className)}
      aria-hidden="true"
      strokeWidth={1.75}
      style={style}
    />
  );
}

/**
 * Legacy string helper — kept for quick-add pending queue fallback.
 * Returns the slug itself (lowercased) so callers can still key off it.
 * New code should use <CategoryEmoji> instead.
 */
export function getCategoryEmoji(category?: string | null): string {
  return category?.toLowerCase() ?? 'other';
}
